import { useEffect, useRef, useState } from 'react';
import { Bot, DollarSign, Package, Sparkles, TrendingUp, Users, Wrench } from 'lucide-react';
import toast from 'react-hot-toast';
import Badge from '../ui/Badge';
import Card from '../ui/Card';
import { supabase } from '../../lib/supabase';
import { getOficinaId } from '../../lib/office';

const AI_SECTORS = {
  financial: {
    icon: DollarSign,
    label: 'Financeiro',
    color: 'bg-green-500',
    prompt: 'Analisar setor financeiro',
    contextType: 'financial_analysis',
  },
  customers: {
    icon: Users,
    label: 'Comercial',
    color: 'bg-blue-500',
    prompt: 'Analisar setor comercial e clientes',
    contextType: 'customer_analysis',
  },
  operational: {
    icon: Wrench,
    label: 'Operacional',
    color: 'bg-orange-500',
    prompt: 'Analisar setor operacional',
    contextType: 'operational_analysis',
  },
  inventory: {
    icon: Package,
    label: 'Estoque',
    color: 'bg-cyan-500',
    prompt: 'Analisar setor de estoque',
    contextType: 'inventory_analysis',
  },
  strategy: {
    icon: TrendingUp,
    label: 'Estratégico',
    color: 'bg-purple-500',
    prompt: 'Analisar estratégia geral da oficina',
    contextType: 'strategy_analysis',
  },
};

function formatAiResponse(data) {
  if (data?.response) return data.response;

  const sections = [];
  if (data?.insights?.length) {
    sections.push(`Insights:\n${data.insights.map((item) => `- ${item}`).join('\n')}`);
  }
  if (data?.recommendations?.length) {
    sections.push(`Recomendações:\n${data.recommendations.map((item) => `- ${item}`).join('\n')}`);
  }

  return sections.join('\n\n') || 'Não foi possível gerar uma resposta.';
}

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Olá! Sou o assistente inteligente da MecSync. Escolha um setor abaixo e eu gero insights usando os dados reais da oficina.',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('strategy');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const generateInsight = async (selectedCategory) => {
    const sector = AI_SECTORS[selectedCategory] || AI_SECTORS.strategy;
    setLoading(true);

    try {
      const oficinaId = await getOficinaId();
      if (!oficinaId) throw new Error('Oficina não identificada');

      const { data: { user } } = await supabase.auth.getUser();

      const response = await supabase.functions.invoke('mecsync-ai', {
        body: {
          category: selectedCategory,
          oficina_id: oficinaId,
          context: { type: sector.contextType, oficina_id: oficinaId },
          user_id: user?.id,
        },
      });

      if (response.error || response.data?.error) {
        throw new Error(response.data?.error || response.error?.message || 'Não foi possível gerar o insight.');
      }

      setMessages((prev) => [
        ...prev,
        { role: 'user', content: sector.prompt },
        { role: 'assistant', content: formatAiResponse(response.data) },
      ]);
    } catch (error) {
      console.error('Erro ao gerar insight:', error);
      toast.error(`Erro ao processar solicitação: ${error.message}`);
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: sector.prompt },
        {
          role: 'assistant',
          content: 'Desculpe, encontrei um erro ao gerar a análise desse setor. Tente novamente ou fale com o suporte.',
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSectorClick = (selectedCategory) => {
    if (loading) return;
    setCategory(selectedCategory);
    generateInsight(selectedCategory);
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-red-500">
              <Bot size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold">MecSync AI</h3>
              <p className="text-xs text-zinc-400">Assistente por setor</p>
            </div>
          </div>
          <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
            <Sparkles size={12} className="mr-1" />
            Beta
          </Badge>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {Object.entries(AI_SECTORS).map(([key, { icon: Icon, label, color }]) => (
            <button
              key={key}
              type="button"
              onClick={() => handleSectorClick(key)}
              disabled={loading}
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition ${
                category === key
                  ? `${color} text-white`
                  : 'bg-surface2 text-zinc-400 hover:text-zinc-200'
              } disabled:cursor-not-allowed disabled:opacity-60`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        <div className="h-96 space-y-4 overflow-y-auto p-4">
          {messages.map((message, index) => (
            <div
              key={`${message.role}-${index}`}
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' && (
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-red-500">
                  <Bot size={16} className="text-white" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-blue-500 text-white'
                    : message.error
                    ? 'bg-red-500/10 text-red-400'
                    : 'bg-surface2'
                }`}
              >
                <p className="whitespace-pre-wrap text-sm">{message.content}</p>
              </div>
              {message.role === 'user' && (
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-500">
                  <span className="text-sm font-medium text-white">VC</span>
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-red-500">
                <Bot size={16} className="text-white" />
              </div>
              <div className="flex items-center gap-1 rounded-2xl bg-surface2 px-4 py-3">
                <div className="h-2 w-2 animate-bounce rounded-full bg-zinc-400" />
                <div className="h-2 w-2 animate-bounce rounded-full bg-zinc-400 delay-100" />
                <div className="h-2 w-2 animate-bounce rounded-full bg-zinc-400 delay-200" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="border-t border-white/10 p-4 text-sm text-zinc-400">
          Escolha um setor acima para gerar uma nova análise. Não há envio de texto livre pelo cliente.
        </div>
      </Card>
    </div>
  );
}
